"use client";

import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  AccountInfo,
  EventType,
  EventMessage
} from "@azure/msal-browser";
import { msalInstance, ADScope, acquireAccessToken, scope } from "../auth/authConfig";
import { mockAuthContext } from "../../tests/mock.data";
import { CLAIMS, UAT_DOMAIN_HOST } from "../utils/constants";
import { Environment } from "../utils/Environment";
import { apiFetch } from "../utils/apiFetch";

interface AuthContextType {
  isReady: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AccountInfo | null;
  roles: string[] | null;
  groups: string[] | null;
  error: string | null;
  login: () => void;
  logout: () => void;
  getToken: (scope?: ADScope) => Promise<string | null>;
  hasRole: (requiredRoles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [roles, setRoles] = useState<string[] | null>(null);
  const [groups, setGroups] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tokenCache, setTokenCache] = useState<Record<string, { token: string; expiresAt: number }>>({});

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      if (!msalInstance) {
        if (isMounted) {
          setError("MSAL instance not available");
          setIsLoading(false);
          setIsReady(true);
        }
        return;
      }

      try {
        // Ensure MSAL is properly initialized before any other MSAL operations
        await msalInstance.initialize();
        console.log('MSAL: Instance initialized');

        await msalInstance.handleRedirectPromise();

        if (!isMounted) return;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          msalInstance.setActiveAccount(account);

          // Force refresh ID token to get latest roles
          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              account: account,
              scopes: [scope(ADScope.AISearchApi)],
              forceRefresh: true,
            });


            // Use the fresh account info from the token response
            const freshAccount = tokenResponse.account;
            msalInstance.setActiveAccount(freshAccount);

            const idTokenClaims = freshAccount.idTokenClaims as {
              roles?: string[];
              groups?: string[];
              [key: string]: unknown;
            };
            const userRoles = idTokenClaims?.roles || [];
            const userGroups = idTokenClaims?.groups || [];

            if (isMounted) {
              setIsAuthenticated(true);
              setUser(freshAccount);
              setRoles(userRoles);
              setGroups(userGroups);
            }
          } catch (tokenError) {
            console.error('Failed to refresh ID token, using cached account:', tokenError);
            // Fallback to cached account if refresh fails
            const idTokenClaims = account.idTokenClaims as {
              roles?: string[];
              groups?: string[];
              [key: string]: unknown;
            };
            const userRoles = idTokenClaims?.roles || [];
            const userGroups = idTokenClaims?.groups || [];

            if (isMounted) {
              setIsAuthenticated(true);
              setUser(account);
              setRoles(userRoles);
              setGroups(userGroups);
            }
          }
        }

        if (isMounted) {
          setIsLoading(false);
          setIsReady(true);
        }
      } catch (e) {
        console.error("Auth initialization error:", e);
        if (isMounted) {
          setError(e instanceof Error ? e.message : "Authentication failed");
          setIsLoading(false);
          setIsReady(true);
        }
      }
    };

    if (msalInstance) {
      msalInstance.addEventCallback((event: EventMessage) => {
        console.log('MSAL: Event received', event.eventType);
        if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          if (!msalInstance) return;

          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            const account = accounts[0];
            msalInstance.setActiveAccount(account);

            const idTokenClaims = account.idTokenClaims as {
              roles?: string[];
              groups?: string[];
              [key: string]: unknown;
            };
            const userRoles = idTokenClaims?.roles || [];
            const userGroups = idTokenClaims?.groups || [];

            setIsAuthenticated(true);
            setUser(account);
            setRoles(userRoles);
            setGroups(userGroups);
            setError(null);
          }
        } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
          setIsAuthenticated(false);
          setUser(null);
          setRoles(null);
          setGroups(null);
          setTokenCache({});
        }
      });
    }

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Wait until auth is ready and user is authenticated
    if (!isReady || !isAuthenticated || roles === null) return;

    // Only redirect in DEV environment — fetch runtime config to determine env
    const checkDevRedirect = async () => {
      let apiEnvironment: string | undefined;
      try {
        const res = await apiFetch('/api/config');
        const data = await res.json();
        apiEnvironment = data.environment?.toUpperCase();
      } catch {
        // If runtime config unavailable, assume non-DEV (safe: won't redirect)
      }
      if (apiEnvironment !== Environment.DEV) return;

      if (!roles!.includes(CLAIMS.DEV_ACCESS)) {
        const redirectUrl = `${UAT_DOMAIN_HOST}${window.location.pathname}${window.location.search}`;
        console.log('User does not have dev-access role, redirecting to UAT:', redirectUrl);
        window.location.replace(redirectUrl);
      }
    };
    checkDevRedirect();
  }, [isReady, isAuthenticated, roles]);

  const login = useCallback(() => {
    if (!msalInstance) return;

    if (msalInstance.getActiveAccount() || msalInstance.getAllAccounts().length > 0) {
      setIsAuthenticated(true);
      return;
    }

    try {
      msalInstance.loginRedirect();
    } catch (e) {
      console.error("Login error:", e);
      setError(e instanceof Error ? e.message : "Login failed");
    }
  }, []);

  const logout = useCallback(() => {
    if (!msalInstance) return;

    const logoutRequest = {
      account: msalInstance.getActiveAccount(),
      postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '',
    };

    msalInstance.logoutRedirect(logoutRequest);
  }, []);

  const getToken = useCallback(async (scope: ADScope = ADScope.AISearchApi): Promise<string | null> => {
    if (!isAuthenticated || !msalInstance) {
      return null;
    }

    const scopeKey = scope.toString();
    const cachedToken = tokenCache[scopeKey];

    if (cachedToken && cachedToken.expiresAt > Date.now()) {
      return cachedToken.token;
    }

    const tokenRequestId = `token-req-${Date.now().toString().slice(-4)}`;

    try {
      setIsLoading(true);
      const token = await acquireAccessToken(scope);

      if (token) {
        const expiresAt = Date.now() + 55 * 60 * 1000;
        setTokenCache(prev => ({
          ...prev,
          [scopeKey]: { token, expiresAt }
        }));

        setError(null);
        return token;
      }

      return null;
    } catch (e) {
      console.error(`[${tokenRequestId}] Token acquisition error:`, e);
      setError(e instanceof Error ? e.message : "Failed to get access token");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, tokenCache]);

  const hasRole = useCallback((requiredRoles: string[]): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    return requiredRoles.some(role => roles?.includes(role));
  }, [roles]);

  const contextValue: AuthContextType = {
    isReady,
    isAuthenticated,
    isLoading,
    user,
    roles,
    groups,
    error,
    login,
    logout,
    getToken,
    hasRole,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  if (process.env.NEXT_PUBLIC_E2E) {
    return mockAuthContext;
  }
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
